import { CalculatorDto, Calculator } from "@/models/calculator.model";
import { Category, CategoryDto } from "@/models/categories.model";
import {
  EquipmentDto,
  Equipment,
  EquipmentType,
  EquipmentTypeDto,
  CategoryEquipmentTypeDto,
  ExtractorDto,
  Extractor,
  RotationTypeDto,
  RotationType,
  AirLockDto,
  AirLock,
  AbaqueDto,
  Point,
  GroupeDto,
} from "@/models/equipment.model";
import { GlobalSettings, GlobalSettingsDto } from "@/models/global.model";
import { AnswserQuestion, Question, QuestionDto } from "@/models/questions.model";
import { ElementaryZone } from "@/models/site.model";  /*Evolution majeure de EACM */

/**
 * Map an array of categoriesDto to an array of categories
 * @param categoryDtos representes category objects from studio
 */
export function mapCategoriesDtoToCategories(categoryDtos: CategoryDto[]): Category[] {
  return categoryDtos.map((dto) => {
    return {
      id: dto.identifiant,
      title: dto.titre,
      description: dto.description,
      image: dto.illustration,
      order: dto.numeroOrdre,
      equipmentType: mapCategoryEquipmentTypeDtoToEquipmentType(dto.typeMateriel),
    };
  });
}

export function mapEquipmentDtosToEquipments(equipmentDtos: EquipmentDto[]): Equipment[] {
  return equipmentDtos.map((dto) => {
    return mapEquipmentDtotoEquipment(dto);
  });
}

function mapEquipmentDtotoEquipment(dto: EquipmentDto): Equipment {
  return {
    id: dto.identifiant,
    completeName: dto.titre,
    model: dto.modele,
    constructor: dto.marque.toUpperCase(),
    description: dto.description,
    order: dto.numeroOrdre,
    image: {
      path: dto.imageLink,
      alt: dto.imageAlt,
    },
    type: mapEquipmentTypeDtoToEquipmentType(dto.typeMateriel),
    dimensions: {
      height: dto.hauteur,
      width: dto.largeur,
      depth: dto.profondeur,
      weight: dto.poids,
    },
    abacus: mapAbaqueDtoToPointList(dto.abaque),
  };
}

//Always a list of 11 points, with first one in 0,0
function mapAbaqueDtoToPointList(dto: AbaqueDto | undefined): Point[] | undefined {
  if (!dto || !dto.groupe1) {
    return;
  } else {
    return [
      { x: 0, y: 0 },
      mapGroupeDtoToPoint(dto.groupe1),
      mapGroupeDtoToPoint(dto.groupe2),
      mapGroupeDtoToPoint(dto.groupe3),
      mapGroupeDtoToPoint(dto.groupe4),
      mapGroupeDtoToPoint(dto.groupe5),
      mapGroupeDtoToPoint(dto.groupe6),
      mapGroupeDtoToPoint(dto.groupe7),
      mapGroupeDtoToPoint(dto.groupe8),
      mapGroupeDtoToPoint(dto.groupe9),
      mapGroupeDtoToPoint(dto.groupe10),
    ].sort((a, b) => a.x - b.x);
  }
}

function mapGroupeDtoToPoint(dto: GroupeDto): Point {
  return { x: Number(dto.pointX), y: Number(dto.pointY) };
}

export function mapExtractorDtosToExtractors(extractorDtos: ExtractorDto[]): Extractor[] {
  return extractorDtos.map((dto) => {
    return {
      ...mapEquipmentDtotoEquipment(dto as EquipmentDto),
      rotation_type: mapRotationTypeDtoToRotationType(dto.vrotation),
      power: dto.puissance,
      noiseLevel: dto.bruit,
      minimumExtraction: dto.debitEncrasse,
      maximumExtraction: dto.debitNeuf,
    };
  });
}

export function mapAirLockDtosToAirLock(airLockDtos: AirLockDto[]): AirLock[] {
  return airLockDtos.map((dto) => {
    return {
      ...mapEquipmentDtotoEquipment(dto as EquipmentDto),
      airLockCount: dto.nbSas,
    };
  });
}

// Use this method when converting equipmentType in equipmentObject
function mapEquipmentTypeDtoToEquipmentType(equipmentTypeDto: EquipmentTypeDto): EquipmentType {
  switch (equipmentTypeDto) {
    case EquipmentTypeDto.sasPersonel:
      return EquipmentType.workersAirLock;
    case EquipmentTypeDto.sasDechets:
      return EquipmentType.wastesAirLock;
    case EquipmentTypeDto.extracteur:
      return EquipmentType.extractor;
    case EquipmentTypeDto.eacm:
      return EquipmentType.eacm;
    case EquipmentTypeDto.ear:
      return EquipmentType.ear;
    //Shouldn't happen
    default:
      return EquipmentType.custom;
  }
}

// Use this method only when converting equipment coming from categories in magnolia
function mapCategoryEquipmentTypeDtoToEquipmentType(
  categoryEquipmentTypeDto: CategoryEquipmentTypeDto
): EquipmentType {
  switch (categoryEquipmentTypeDto) {
    case CategoryEquipmentTypeDto.sasPersonel:
      return EquipmentType.workersAirLock;
    case CategoryEquipmentTypeDto.sasDechets:
      return EquipmentType.wastesAirLock;
    case CategoryEquipmentTypeDto.extracteur:
      return EquipmentType.extractor;
    case CategoryEquipmentTypeDto.eacm:
      return EquipmentType.eacm;
    case CategoryEquipmentTypeDto.ear:
      return EquipmentType.ear;
    //Shouldn't happen
    default:
      return EquipmentType.custom;
  }
}

function mapRotationTypeDtoToRotationType(rotationTypeDto: RotationTypeDto): RotationType {
  switch (rotationTypeDto) {
    case RotationTypeDto.fixe:
      return RotationType.set;
    case RotationTypeDto.variable:
      return RotationType.variable;
    //Shouldn't happen
    default:
      return RotationType.variable;
  }
}

/**
 * Map base questions to questions
 * @param questions representes base questions
 */
export function mapQuestionDtoToQuestions(questions: QuestionDto[]): Question[] {
  return questions
    .map((question) => {
      return {
        ref: question.identifiant,
        order: question.numeroOrdre,
        text: question.texte,
      };
    })
    .sort((a, b) => a.order - b.order);
}

/**
 * Map base questions to questions
 * @param questions representes base questions
 */
export function mapCalculatorDtoToCalculator(calculators: CalculatorDto[]): Calculator[] {
  return calculators.map((calculator) => {
    return {
      id: calculator.identifiant,
      title: calculator.titre,
      description: calculator.description,
      code: calculator.code,
    };
  });
}

/**
 * Map base settings
 * @param settings representes base settings
 */
export function mapSettingsDtoToSettings(settings: GlobalSettingsDto): GlobalSettings {
  return {
    maxZoneNumber: settings.nbZonesElemMax,
    maxZoneVolume: settings.volMaxZoneElem,
    maxTotalVolume: settings.volTotalMaxZoneConf,
    maximumRenewalRate: settings.tauxRenouvMax,
    minimumRenewalRate: settings.tauxRenouvMin,
    defaultDepression: settings.depChoixDefaut,
    minimumDepression: settings.depMin,
    maximumDepression: settings.depChoixMax,
    dustLevelMax: settings.nivEmpMax,
    dustLevelMin: settings.nivEmpMin,
    maximumLeakageRateDeviation: settings.ecartMaxTauxFuite,
  };
}

/**
 * Map question to answer
 * @param settings representes base settings
 */
export function mapQuestionDtoToAnswers(questionsDto: QuestionDto[]): AnswserQuestion[] {
  const questions = mapQuestionDtoToQuestions(questionsDto);
  return questions.map((question) => {
    return {
      question: question,
      value: false,
    };
  });
}
